import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { EngagementService } from "@/services/engagement-service";
import { apiService, AuditClient, Engagement } from "@/services/api-service";
import { useAuth } from "@/contexts/AuthContext";
// SharePoint operations will be done via Python script
import {
  EngagementFormData,
  EngagementType,
  FinancialReportingFramework,
  Client,
} from "@/types/engagement";
import { Loader2, ChevronDown, ChevronUp, RotateCcw } from "lucide-react";
import { cn } from "@/utils/tailwind";

interface EngagementCreationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (engagement: any) => void;
}

export function EngagementCreationModal({
  isOpen,
  onClose,
  onSuccess,
}: EngagementCreationModalProps) {
  const [formData, setFormData] = useState<EngagementFormData>({
    clientId: "",
    clientName: "",
    engagementName: "",
    typeOfEngagement: "Financial statement audit",
    financialReportingFramework: undefined,
    reportingDate: undefined,
    rollforwardedFromFileId: undefined,
  });

  const { user } = useAuth();
  const [allClients, setAllClients] = useState<AuditClient[]>([]);
  const [selectedClient, setSelectedClient] = useState<AuditClient | null>(null);
  const [availableArchivedFiles, setAvailableArchivedFiles] = useState<any[]>([]);
  const [frameworkOptions, setFrameworkOptions] = useState<{
    isFirstTime: boolean;
    options: FinancialReportingFramework[];
  }>({ isFirstTime: true, options: [] });
  const [isLoadingClients, setIsLoadingClients] = useState(false);

  const [archivedEngagementsForRF, setArchivedEngagementsForRF] = useState<Engagement[]>([]);
  const [isLoadingArchivedRF, setIsLoadingArchivedRF] = useState(false);
  const [showRollforwardDropdown, setShowRollforwardDropdown] = useState(false);
  const [selectedRollforwardEngagementId, setSelectedRollforwardEngagementId] = useState<string>("");

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load clients when modal opens
  useEffect(() => {
    if (isOpen && user) {
      loadClients();
    }
  }, [isOpen, user]);

  const loadClients = async () => {
    try {
      setIsLoadingClients(true);
      console.log("Loading clients for user:", user?.user_name, user?.email, user?.id, user?.type);

      // Get clients where user is engagement_partner or engagement_manager
      // This should get clients through engagements
      const clients = await apiService.getUserClients();
      console.log("Clients from getUserClients:", clients);
      console.log("Number of clients:", clients?.length || 0);

      // Filter clients to only show:
      // 1. Status is 'active' (case-insensitive)
      // 2. User is engagement_partner or engagement_manager (already filtered by backend)
      const filteredClients = (clients || []).filter(client => {
        const isActive = client.status && client.status.toLowerCase() === 'active';
        if (!isActive) {
          console.log(`Filtering out client ${client.client_name} - status: ${client.status}`);
        }
        return isActive;
      });

      console.log(`Filtered to ${filteredClients.length} active clients (out of ${clients?.length || 0} total)`);

      if (filteredClients && filteredClients.length > 0) {
        setAllClients(filteredClients);
        console.log("Successfully loaded", filteredClients.length, "active clients");
        return;
      }

      // If we get here, no active clients were found
      console.warn("No active clients found for user");
      setAllClients([]);
    } catch (error: any) {
      console.error("Error loading clients:", error);
      console.error("Error message:", error?.message);
      console.error("Error stack:", error?.stack);

      setAllClients([]);
    } finally {
      setIsLoadingClients(false);
    }
  };

  // Load framework options when client is selected
  useEffect(() => {
    if (formData.clientId) {
      const options = EngagementService.getAvailableFrameworks(formData.clientId);
      setFrameworkOptions(options);

      // Auto-select framework if only one option or if it's a subsequent audit
      if (options.options.length === 0 && !options.isFirstTime) {
        // For subsequent audits where framework is already determined
        const previousFramework = EngagementService.getClientPreviousFramework(formData.clientId);
        if (previousFramework === "Transition to Ind AS") {
          setFormData((prev) => ({
            ...prev,
            financialReportingFramework: "Indian Accounting Standards (Ind AS)",
          }));
        } else if (previousFramework === "Indian Accounting Standards (Ind AS)") {
          setFormData((prev) => ({
            ...prev,
            financialReportingFramework: "Indian Accounting Standards (Ind AS)",
          }));
        }
      } else if (options.options.length === 1) {
        setFormData((prev) => ({
          ...prev,
          financialReportingFramework: options.options[0],
        }));
      }

      // Load available archived files
      const files = EngagementService.getAvailableArchivedFiles(formData.clientId);
      setAvailableArchivedFiles(files);
    } else {
      setFrameworkOptions({ isFirstTime: true, options: [] });
      setAvailableArchivedFiles([]);
    }
  }, [formData.clientId]);


  const loadArchivedEngagements = async (clientId: string) => {
    try {
      setIsLoadingArchivedRF(true);
      const engagements = await apiService.getArchivedEngagementsForRollforward(clientId);
      setArchivedEngagementsForRF(engagements);
    } catch (error: any) {
      console.error("Error loading archived engagements for rollforward:", error);
      setArchivedEngagementsForRF([]);
    } finally {
      setIsLoadingArchivedRF(false);
    }
  };

  const handleClientSelect = (client: AuditClient) => {
    setSelectedClient(client);
    setFormData((prev) => ({
      ...prev,
      clientId: client.id,
      clientName: client.client_name,
    }));
    // Reset rollforward state when client changes
    setShowRollforwardDropdown(false);
    setSelectedRollforwardEngagementId("");
    setArchivedEngagementsForRF([]);
    loadArchivedEngagements(client.id);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    // Validation
    const newErrors: Record<string, string> = {};

    if (!formData.engagementName || !formData.engagementName.trim()) {
      newErrors.engagementName = "Engagement name is required";
    }

    if (!formData.clientId || !formData.clientName) {
      newErrors.client = "Client is required";
    }

    // Type and Framework are not required when rolling forward from an archived engagement
    if (!selectedRollforwardEngagementId) {
      if (!formData.typeOfEngagement) {
        newErrors.typeOfEngagement = "Type of engagement is required";
      }

      if (frameworkOptions.isFirstTime && !formData.financialReportingFramework) {
        newErrors.financialReportingFramework = "Financial Reporting Framework is required";
      } else if (!frameworkOptions.isFirstTime && frameworkOptions.options.length > 0 && !formData.financialReportingFramework) {
        newErrors.financialReportingFramework = "Financial Reporting Framework is required";
      }
    }

    if (!formData.reportingDate) {
      newErrors.reportingDate = "Reporting Date is required";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      if (!formData.clientId) {
        setErrors({ client: "Client is required" });
        setIsSubmitting(false);
        return;
      }

      // Step 0: Check for duplicate engagement name BEFORE any SharePoint operations
      console.log('[Engagement Creation] Step 0: Checking for duplicate engagement name...');
      const nameExists = await apiService.checkEngagementNameExists(
        formData.clientId,
        formData.engagementName || ""
      );
      if (nameExists) {
        setErrors({
          engagementName: `An engagement named "${formData.engagementName}" already exists for this client. Please choose a different name.`
        });
        setIsSubmitting(false);
        return;
      }
      console.log('[Engagement Creation] Step 0: Name is available, proceeding...');

      // Step 1: Get firm configuration for SharePoint
      const firm = await apiService.getFirm();

      if (!firm.tenant_id || !firm.client_id || !firm.client_secret) {
        throw new Error("Firm SharePoint configuration is incomplete. Please contact your administrator.");
      }

      // Step 2: Get engagement partner and manager emails
      const emails = await apiService.getClientEngagementEmails(formData.clientId);

      // Step 3: Prepare SharePoint configuration
      const sharePointConfig = {
        tenant_id: firm.tenant_id,
        client_id: firm.client_id,
        client_secret: firm.client_secret,
        site_hostname: firm.site_hostname || "",
        site_path: firm.site_path || "",
      };

      // Step 4: Format reporting date (convert from ISO format YYYY-MM-DD to DD-MM-YYYY)
      let repDate = "";
      if (formData.reportingDate) {
        // formData.reportingDate is in ISO format (YYYY-MM-DD) from the date picker
        const dateParts = formData.reportingDate.split("-");
        if (dateParts.length === 3) {
          const [year, month, day] = dateParts;
          repDate = `${day}-${month}-${year}`;
        } else {
          // Fallback to Date object parsing if format is different
          const date = new Date(formData.reportingDate);
          const day = String(date.getDate()).padStart(2, "0");
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const year = date.getFullYear();
          repDate = `${day}-${month}-${year}`;
        }
      }

      // Step 5: Run SharePoint operations via Python script BEFORE creating engagement
      console.log('[Engagement Creation] Step 5: Starting SharePoint operations...');
      const engagementAPI = (window as any).engagementAPI;
      if (!engagementAPI || !engagementAPI.createEngagementInSharePoint) {
        throw new Error('Engagement API not available. Make sure you are running in an Electron environment.');
      }

      console.log('[Engagement Creation] Calling createEngagementInSharePoint...');
      const sharePointResult = await engagementAPI.createEngagementInSharePoint({
        tenant_id: sharePointConfig.tenant_id,
        client_id: sharePointConfig.client_id,
        client_secret: sharePointConfig.client_secret,
        site_hostname: sharePointConfig.site_hostname,
        site_path: sharePointConfig.site_path,
        doc_library: formData.clientName, // doc_library = client name
        engagement_name: formData.engagementName || "", // engagement_name
        engagement_type: formData.typeOfEngagement, // engagement_type
        frf: formData.financialReportingFramework || "", // frf
        rep_date: repDate, // rep_date
        emails: emails, // emails array
      });

      console.log('[Engagement Creation] SharePoint result received:', JSON.stringify(sharePointResult, null, 2));
      console.log('[Engagement Creation] sharePointResult.success:', sharePointResult.success);
      console.log('[Engagement Creation] sharePointResult.error:', sharePointResult.error);

      // Handle SharePoint result:
      // - If success is true → proceed.
      // - If success is false BUT error is only about sharing/invite permissions,
      //   we log a warning and still proceed to create the engagement in UserAuth.
      if (!sharePointResult.success) {
        const errorMessage: string = sharePointResult.error || "";
        console.log('[Engagement Creation] SharePoint success=false, checking error message:', errorMessage);

        const isSharePermissionIssue =
          errorMessage.toLowerCase().includes("sharingfailed") ||
          errorMessage.toLowerCase().includes("permission error") ||
          errorMessage.toLowerCase().includes("there was a problem sharing") ||
          errorMessage.includes("400") ||
          errorMessage.includes("Bad Request");

        console.log('[Engagement Creation] Is share permission issue?', isSharePermissionIssue);

        if (isSharePermissionIssue) {
          console.warn(
            "[Engagement Creation] SharePoint sharing/invite error occurred, but folder and files were uploaded. Proceeding with engagement creation.",
            errorMessage
          );
        } else {
          console.error('[Engagement Creation] Fatal SharePoint error, throwing:', errorMessage);
          throw new Error(errorMessage || "SharePoint operation failed");
        }
      } else {
        console.log('[Engagement Creation] SharePoint operation successful');
      }

      // Step 5.5 (Rollforward only): Copy files from archived engagement folder to the new folder
      if (selectedRollforwardEngagementId) {
        const sourceEngagement = archivedEngagementsForRF.find(
          (e) => e.id === selectedRollforwardEngagementId
        );
        const sourceEngagementName = sourceEngagement?.engagement_name;

        if (!sourceEngagementName) {
          throw new Error("Could not determine the source archived engagement name for rollforward.");
        }

        // rep_date for rollforward script uses DD/MM/YYYY (slash) format
        let rfRepDate = "";
        if (formData.reportingDate) {
          const dateParts = formData.reportingDate.split("-");
          if (dateParts.length === 3) {
            const [year, month, day] = dateParts;
            rfRepDate = `${day}/${month}/${year}`;
          }
        }

        console.log('[Engagement Creation] Step 5.5: Running rollforward copy...');
        console.log('[Engagement Creation] Source:', sourceEngagementName, '→ Dest:', formData.engagementName);

        if (!engagementAPI.rollforwardEngagement) {
          throw new Error('rollforwardEngagement API not available. Make sure you are running in an Electron environment.');
        }

        const rollforwardResult = await engagementAPI.rollforwardEngagement({
          tenant_id: sharePointConfig.tenant_id,
          client_id: sharePointConfig.client_id,
          client_secret: sharePointConfig.client_secret,
          site_hostname: sharePointConfig.site_hostname,
          site_path: sharePointConfig.site_path,
          doc_library: formData.clientName,
          fy_year: sourceEngagementName,
          new_fy_year: formData.engagementName || "",
          rep_date: rfRepDate,
        });

        console.log('[Engagement Creation] Rollforward result:', JSON.stringify(rollforwardResult, null, 2));

        if (!rollforwardResult.success) {
          throw new Error(rollforwardResult.error || "Rollforward SharePoint copy failed");
        }

        console.log('[Engagement Creation] Rollforward copy completed successfully');

        // Mark the source archived engagement as rollforwarded
        console.log('[Engagement Creation] Marking source engagement is_rf = true:', selectedRollforwardEngagementId);
        await apiService.updateEngagement(selectedRollforwardEngagementId, { is_rf: true });
        console.log('[Engagement Creation] Source engagement marked as rollforwarded');
      }

      // Step 6: Create engagement via API (after SharePoint operations succeed / or non-fatal SharePoint errors)
      // Note: The API expects engagement_partner_id and engagement_manager_id
      // These should be automatically assigned based on the default engagement logic
      console.log('[Engagement Creation] Step 6: Creating engagement in database...');
      console.log('[Engagement Creation] Calling apiService.createEngagement with clientId:', formData.clientId, 'engagementName:', formData.engagementName);
      const engagement = await apiService.createEngagement(formData.clientId, formData.engagementName);
      console.log('[Engagement Creation] Engagement created successfully:', engagement);

      // Step 7: Update engagement with additional details if needed
      // Note: The API might need to be updated to accept these fields during creation
      // For now, we'll create the engagement and SharePoint operations are done

      console.log('[Engagement Creation] Calling onSuccess with engagement...');
      onSuccess(engagement);
      // Reset form
      setFormData({
        clientId: "",
        clientName: "",
        engagementName: "",
        typeOfEngagement: "Financial statement audit",
        financialReportingFramework: undefined,
        reportingDate: undefined,
        rollforwardedFromFileId: undefined,
      });
      setSelectedClient(null);
      setArchivedEngagementsForRF([]);
      setShowRollforwardDropdown(false);
      setSelectedRollforwardEngagementId("");
      console.log('[Engagement Creation] Closing modal...');
      onClose();
    } catch (error: any) {
      console.error("[Engagement Creation] ERROR:", error);
      console.error("[Engagement Creation] Error message:", error.message);
      console.error("[Engagement Creation] Error stack:", error.stack);
      setErrors({ submit: error.message || "Failed to create engagement. Please try again." });
    } finally {
      console.log('[Engagement Creation] Finally block - setting isSubmitting to false');
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-black/95 border-white/10 duration-500 data-[state=open]:slide-in-from-top-[40%] data-[state=open]:zoom-in-90">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-white">
            Create New Engagement
          </DialogTitle>
          <DialogDescription className="text-white/60">
            Fill in the details below to create a new engagement
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* Engagement Name */}
          <div className="space-y-2">
            <Label htmlFor="engagementName" className="text-white">
              Engagement Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="engagementName"
              type="text"
              placeholder="Enter engagement name..."
              value={formData.engagementName || ""}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, engagementName: e.target.value }))
              }
              className={cn(
                "border-white/10 bg-black/40 text-white",
                errors.engagementName && "border-red-500"
              )}
            />
            {errors.engagementName && (
              <p className="text-sm text-red-500">{errors.engagementName}</p>
            )}
          </div>

          {/* Client Name */}
          <div className="space-y-2">
            <Label htmlFor="client" className="text-white">
              Client Name <span className="text-red-500">*</span>
            </Label>
            {isLoadingClients ? (
              <div className="flex items-center gap-2 text-white/60">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm">Loading clients...</span>
              </div>
            ) : allClients.length === 0 ? (
              <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-md">
                <p className="text-sm text-yellow-400">
                  No active clients found. Please ensure you are assigned as engagement_partner or engagement_manager to at least one active client.
                </p>
              </div>
            ) : (
              <Select
                value={formData.clientId || ""}
                onValueChange={(value) => {
                  const client = allClients.find(c => c.id === value);
                  if (client) {
                    handleClientSelect(client);
                  }
                }}
              >
                <SelectTrigger
                  id="client"
                  className={cn(
                    "border-white/10 bg-black/40 text-white",
                    errors.client && "border-red-500"
                  )}
                >
                  <SelectValue placeholder="Select a client..." />
                </SelectTrigger>
                <SelectContent className="bg-black/95 border-white/10 max-h-60">
                  {allClients.map((client) => (
                    <SelectItem key={client.id} value={client.id} className="cursor-pointer">
                      <div className="flex flex-col py-1">
                        <span className="font-medium text-white">{client.client_name}</span>
                        <span className="text-xs text-white/60">Status: {client.status}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {errors.client && (
              <p className="text-sm text-red-500">{errors.client}</p>
            )}
            {selectedClient && (
              <div className="mt-2 p-3 bg-blue-500/10 border border-blue-500/30 rounded-md">
                <div className="text-sm text-white">
                  <div className="font-medium">Selected: {selectedClient.client_name}</div>
                  <div className="text-white/60 mt-1">Status: {selectedClient.status}</div>
                </div>
              </div>
            )}
          </div>

          {/* Rollforward an Archived Engagement */}
          {formData.clientId && (
            <div className="space-y-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowRollforwardDropdown((prev) => !prev)}
                disabled={isLoadingArchivedRF}
                className="w-full flex items-center justify-between border-amber-500/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 hover:text-amber-200 hover:border-amber-500/60"
              >
                <span className="flex items-center gap-2">
                  {isLoadingArchivedRF ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RotateCcw className="h-4 w-4" />
                  )}
                  {isLoadingArchivedRF ? "Loading archived engagements..." : "Rollforward an Archived Engagement"}
                </span>
                {!isLoadingArchivedRF && (
                  showRollforwardDropdown ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )
                )}
              </Button>

              {showRollforwardDropdown && !isLoadingArchivedRF && (
                <div className="border border-amber-500/20 rounded-md bg-amber-500/5 p-3 space-y-2">
                  {archivedEngagementsForRF.length === 0 ? (
                    <p className="text-sm text-white/50 text-center py-2">
                      No archived engagements available to rollforward for this client.
                    </p>
                  ) : (
                    <>
                      <Label className="text-white/80 text-sm">
                        Select archived engagement to rollforward from
                      </Label>
                      <Select
                        value={selectedRollforwardEngagementId}
                        onValueChange={(value) =>
                          setSelectedRollforwardEngagementId(value)
                        }
                      >
                        <SelectTrigger className="border-amber-500/30 bg-black/40 text-white">
                          <SelectValue placeholder="Select an archived engagement..." />
                        </SelectTrigger>
                        <SelectContent className="bg-black/95 border-white/10 max-h-60">
                          {archivedEngagementsForRF.map((eng) => (
                            <SelectItem key={eng.id} value={eng.id} className="cursor-pointer">
                              <span className="text-white">{eng.engagement_name}</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {selectedRollforwardEngagementId && (
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-amber-400">
                            Selected:{" "}
                            {archivedEngagementsForRF.find(
                              (e) => e.id === selectedRollforwardEngagementId
                            )?.engagement_name}
                          </p>
                          <button
                            type="button"
                            onClick={() => setSelectedRollforwardEngagementId("")}
                            className="text-xs text-white/40 hover:text-white/70 underline"
                          >
                            Clear
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Type of Engagement */}
          <div className="space-y-2">
            <Label htmlFor="typeOfEngagement" className={cn("text-white", selectedRollforwardEngagementId && "text-white/40")}>
              Type of Engagement {!selectedRollforwardEngagementId && <span className="text-red-500">*</span>}
            </Label>
            <Select
              value={formData.typeOfEngagement}
              onValueChange={(value: EngagementType) =>
                setFormData((prev) => ({ ...prev, typeOfEngagement: value }))
              }
              disabled={!!selectedRollforwardEngagementId}
            >
              <SelectTrigger
                id="typeOfEngagement"
                className={cn(
                  "border-white/10 bg-black/40 text-white",
                  errors.typeOfEngagement && "border-red-500",
                  selectedRollforwardEngagementId && "opacity-40 cursor-not-allowed"
                )}
              >
                <SelectValue placeholder="Select engagement type" />
              </SelectTrigger>
              <SelectContent className="bg-black/95 border-white/10">
                <SelectItem value="Financial statement audit">
                  Financial statement audit
                </SelectItem>
                <SelectItem value="Limited Review">Limited Review</SelectItem>
                <SelectItem value="Other Engagement">Other Engagement</SelectItem>
              </SelectContent>
            </Select>
            {selectedRollforwardEngagementId && (
              <p className="text-xs text-amber-400/70">Inherited from archived engagement during rollforward</p>
            )}
            {errors.typeOfEngagement && (
              <p className="text-sm text-red-500">{errors.typeOfEngagement}</p>
            )}
          </div>

          {/* Rollforward Archived File */}
          {formData.clientId && availableArchivedFiles.length > 0 && (
            <div className="space-y-2">
              <Label htmlFor="rollforward" className="text-white">
                Rollforward Archived File (Optional)
              </Label>
              <Select
                value={formData.rollforwardedFromFileId || ""}
                onValueChange={(value) =>
                  setFormData((prev) => ({
                    ...prev,
                    rollforwardedFromFileId: value || undefined,
                  }))
                }
              >
                <SelectTrigger
                  id="rollforward"
                  className="border-white/10 bg-black/40 text-white"
                >
                  <SelectValue placeholder="Select archived file to rollforward" />
                </SelectTrigger>
                <SelectContent className="bg-black/95 border-white/10">
                  <SelectItem value="">None</SelectItem>
                  {availableArchivedFiles.map((file) => (
                    <SelectItem key={file.id} value={file.id}>
                      {file.fileName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Financial Reporting Framework */}
          {(frameworkOptions.isFirstTime || frameworkOptions.options.length > 0) && !selectedRollforwardEngagementId ? (
            <div className="space-y-2">
              <Label htmlFor="framework" className="text-white">
                Financial Reporting Framework{" "}
                {frameworkOptions.isFirstTime && <span className="text-red-500">*</span>}
              </Label>
              <Select
                value={formData.financialReportingFramework || ""}
                onValueChange={(value: FinancialReportingFramework) =>
                  setFormData((prev) => ({
                    ...prev,
                    financialReportingFramework: value,
                  }))
                }
                disabled={!frameworkOptions.isFirstTime && frameworkOptions.options.length === 0}
              >
                <SelectTrigger
                  id="framework"
                  className={cn(
                    "border-white/10 bg-black/40 text-white",
                    errors.financialReportingFramework && "border-red-500"
                  )}
                >
                  <SelectValue placeholder="Select framework" />
                </SelectTrigger>
                <SelectContent className="bg-black/95 border-white/10">
                  {frameworkOptions.options.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.financialReportingFramework && (
                <p className="text-sm text-red-500">{errors.financialReportingFramework}</p>
              )}
              {!frameworkOptions.isFirstTime && frameworkOptions.options.length === 0 && (
                <p className="text-sm text-white/60">
                  Framework is automatically set based on previous engagement
                </p>
              )}
            </div>
          ) : null}

          {/* Reporting Date */}
          <div className="space-y-2">
            <Label htmlFor="reportingDate" className="text-white">
              Reporting Date as per Balance Sheet <span className="text-red-500">*</span>
            </Label>
            <Input
              id="reportingDate"
              type="date"
              value={formData.reportingDate}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, reportingDate: e.target.value }))
              }
              className={cn("bg-black/40 border-white/10 text-white", errors.reportingDate && "border-red-500")}
            />
            {errors.reportingDate && (
              <p className="text-sm text-red-500">{errors.reportingDate}</p>
            )}
          </div>

          {errors.submit && (
            <div className="p-3 bg-red-500/20 border border-red-500/50 rounded-md">
              <p className="text-sm text-red-400">{errors.submit}</p>
            </div>
          )}

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
              className="border-white/10 text-white hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isSubmitting ? "Creating..." : "Create Engagement"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

